import { useContext, useMemo } from 'react'
import * as Constants from '../services/Constants'
import {
    AudioInputSettings,
    AudioOutputSettings,
    AudioSourceInfo,
    DeviceId,
    UserContext,
    UserContextType,
} from '../UserContext'
import useUserContextRef from '../services/useUserContextRef'
import { logger } from '../services/Logger'
import AudioSettingsStore from './AudioSettingsStore'
import { getDefaultAudioDevicePreset } from '../Logic'

const log = logger.createChildLogger({ name: 'AudioSettingsStoreActions' })

function shouldEnableLocalLoopbackDefault(source: AudioSourceInfo | null) {
    return !!source?.isMuseAudioShare
}

export default function useAudioSettingsStoreActions() {
    const { dispatch } = useContext(UserContext) as UserContextType

    const { userStateRef } = useUserContextRef()

    return useMemo(() => {
        const load = () => {
            const { audioInputs, audioOutputs } = AudioSettingsStore.getSettingsLookupByDeviceType()

            dispatch({
                type: 'update_media_settings',
                value: {
                    audioInputs,
                    audioOutputs,
                },
            })
        }

        const getAudioInputSettings = (source: AudioSourceInfo, hint?: { isTalkbackMic?: boolean }) => {
            const settings = AudioSettingsStore.get(source.deviceId, 'AudioInputDevice') as AudioInputSettings | null
            if (settings && !hint?.isTalkbackMic) {
                return settings
            }

            const defaultPreset = getDefaultAudioDevicePreset(source, hint)

            // create default settings using the source.
            const newSettings: AudioInputSettings = {
                sampleRate: source.defaultSampleRate || Constants.defaultAudioInputSettings.sampleRate,
                bitDepth: Constants.defaultAudioInputSettings.bitDepth,
                channelSelection: [],
                isCompressed: true,
                compressionBitrate: 192000,
                isAudioEngine: defaultPreset?.isPro ?? false,
                isHighQuality: !defaultPreset || defaultPreset.code === 'voice' ? false : true,
                receiverBufferSizeMs: 250,
                receiverBufferMaxDriftMs: 0, // 0 indicates not used
                framesPerBuffer: 0,
                networkPacketDurationMs: 0, // default legacy values (20ms pcm, 40ms opus)
                enableLocalLoopback: shouldEnableLocalLoopbackDefault(source),
                isVoiceQuality: defaultPreset?.isVoice ?? false,
            }
            if (source.maxInputChannels === 0) {
                newSettings.channelSelection = []
            } else if (source.maxInputChannels === 1) {
                newSettings.channelSelection = [[0]]
            } else {
                newSettings.channelSelection = [[0, 1]]
            }

            AudioSettingsStore.set(source.deviceId, 'AudioInputDevice', newSettings)

            return newSettings
        }

        const setAudioInputSettings = (deviceId: DeviceId, settings: AudioInputSettings) => {
            AudioSettingsStore.set(deviceId, 'AudioInputDevice', settings)

            const audioInputsNext = { ...userStateRef.current.mediaSettings.audioInputs }
            audioInputsNext[deviceId] = settings

            dispatch({
                type: 'update_media_settings',
                value: {
                    audioInputs: audioInputsNext,
                },
            })
        }

        const getDefaultAudioOutputSettings = (deviceId: DeviceId) => {
            const source =
                userStateRef.current.mediaSources.audioOutputsAll.find((a) => a.deviceId === deviceId) || null
            if (!source) {
                log.assert(
                    'getAudioOutputSettings',
                    `attempted to select an audio output device that ` +
                        `does not exist in userState.mediaSources. deviceId="${deviceId}", mediaSources.audioOutputs=` +
                        `${JSON.stringify(userStateRef.current.mediaSources.audioOutputsAll)}`
                )
                return null
            }

            return {
                sampleRate: source.defaultSampleRate || Constants.defaultAudioOutputSettings.sampleRate,
                bitDepth: Constants.defaultAudioOutputSettings.bitDepth,
                channelCount: source.maxOutputChannels || Constants.defaultAudioOutputSettings.channelCount,
                compressionBitrate: 0,
                isSelective: false,
                includeStreams: [],
                excludeStreams: [],
            } as AudioOutputSettings
        }

        const getAudioOutputSettings = (deviceId: DeviceId) => {
            const settings = AudioSettingsStore.get(deviceId, 'AudioOutputDevice') as AudioOutputSettings | null
            if (settings) {
                return settings
            }

            const newSettings = getDefaultAudioOutputSettings(deviceId)
            if (newSettings) {
                AudioSettingsStore.set(deviceId, 'AudioOutputDevice', newSettings)
            }

            return newSettings
        }

        const setAudioOutputSettings = (deviceId: DeviceId, settings: AudioOutputSettings) => {
            AudioSettingsStore.set(deviceId, 'AudioOutputDevice', settings)

            const audioOutputsNext = { ...userStateRef.current.mediaSettings.audioOutputs }
            audioOutputsNext[deviceId] = settings

            dispatch({
                type: 'update_media_settings',
                value: {
                    audioOutputs: audioOutputsNext,
                },
            })
        }

        return {
            load,
            getAudioInputSettings,
            setAudioInputSettings,
            getAudioOutputSettings,
            getDefaultAudioOutputSettings,
            setAudioOutputSettings,
        }
    }, [])
}
